/**
 * サイドバーのコンポーネント
 */
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * ナビゲーション項目
 * @typedef {Object} NavItem
 * @property {string} path - ナビゲーションリンクのURL
 * @property {string} label - ナビゲーションリンクのラベル
 * @property {NavItem[]} [children] - 子ナビゲーションアイテムのリスト（オプション）
 */
interface NavItem {
  path: string;
  label: string;
  children?: NavItem[];
}

/**
 * サイドバーの項目
 * @typedef {Object} SideNavProps
 * @property {NavItem[]} items - サイドバーの項目
 * @property {string[]} trustedUrls - 信頼できるURLのリスト
 * @property {Function} onNavClick - ナビゲーションリンクがクリックされたときのハンドラ
 */
interface SideNavProps {
  items: NavItem[];
  trustedUrls: string[];
}

/**
 * サイドバーコンポーネント
 * @param {SideNavProps} props - プロパティ
 * @returns {JSX.Element} サイドバーのJSX要素
 */
export function SideNav({ items, trustedUrls }: SideNavProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * ナビゲーションリンクがクリックされたときのハンドラ
   * @param {string} href - クリックされたリンクのURL
   * @param {number} index - クリックされたリンクのインデックス
   */
  const handleNavClick = (href: string, index: number) => {
    if (trustedUrls.includes(href)) {
      console.log("Navigated to:", findLabelByIndex(items, index));
      navigate(href);
    } else {
      console.warn("Untrusted URL:", href);
    }
  };

  /**
   * インデックスからラベルを検索する
   * @param {NavItem[]} items - ナビゲーション項目のリスト
   * @param {number} index - 検索するインデックス
   * @param {number} [currentIndex=0] - 現在のインデックス
   * @returns {string | null} 見つかったラベル、またはnull
   */
  const findLabelByIndex = (
    items: NavItem[],
    index: number,
    currentIndex: number = 0
  ): string | null => {
    for (const item of items) {
      if (currentIndex === index) {
        return item.label;
      }
      currentIndex++;
      if (item.children) {
        const label = findLabelByIndex(item.children, index, currentIndex);
        if (label) {
          return label;
        }
        currentIndex += item.children.length;
      }
    }
    return null;
  };

  /**
   * パスがアクティブかどうかを判定する
   * @param {string} path - 判定するパス
   * @returns {boolean} パスがアクティブかどうか
   */
  const isPathActive = (path: string): boolean => {
    return location.pathname.startsWith(path);
  };

  /**
   * ナビゲーション項目をレンダリングする
   * @param {NavItem[]} items - ナビゲーション項目のリスト
   * @param {number} [parentIndex=0] - 親項目のインデックス
   * @param {number} [level=0] - 階層レベル
   * @returns {JSX.Element[]} レンダリングされたナビゲーション項目のリスト
   */
  const renderNavItems = (
    items: NavItem[],
    parentIndex: number = 0,
    level: number = 0
  ): JSX.Element[] => {
    return items.map((item, index) => {
      const isActive = location.pathname === item.path;
      const hasChildren = item.children && item.children.length > 0;
      const liClassName = level === 1 ? "p-side-nav__child" : "";

      // 子クラスまたは孫クラスがアクティブな時だけ表示
      const shouldRenderChildren =
        hasChildren && item.children!.some((child) => isPathActive(child.path));

      return (
        <React.Fragment key={`${parentIndex}-${index}`}>
          <li className={`${liClassName} ${isActive ? "is-active" : ""}`}>
            <a
              className="c-angle"
              href={item.path}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(item.path, parentIndex + index);
              }}
            >
              {item.label}
            </a>
          </li>
          {shouldRenderChildren &&
            renderNavItems(item.children!, parentIndex + index + 1, 1)}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="p-side-nav__group">
      <ul>{renderNavItems(items)}</ul>
    </div>
  );
}
